// pages/product/CityVariant.jsx
import { useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiArrowRight } from "react-icons/fi";
import toast from "react-hot-toast";
import { useSEO } from "../../hooks/useSEO";
import { CITY_VARIANT_ROUTE, CALCULATOR_ROUTE } from "../../routes/constant";
import {
  fetchProductDetails,
  clearProductData,
  setSelectedVariant,
} from "../../store/slices/productSlice";
import { findCityBySlug } from "../../store/slices/citySlice";
import { useTheme } from "../../theme/ThemeProvider";
import Layout from "../../components/Layout";
import ProductImage from "../../components/product/ProductImage";
import ProductInfo from "../../components/product/ProductInfo";
import ProductVariants from "../../components/product/ProductVariants";
import ProductPriceCard from "../../components/product/ProductPriceCard";
import ProductFeatures from "../../components/product/ProductFeatures";
import ProductSkeleton from "../../components/product/ProductSkeleton";
import ErrorState from "../../components/state/ErrorState";
import EmptyState from "../../components/state/EmptyState";

const CityVariant = () => {
  const dispatch = useDispatch();
  const { citySlug, categorySlug, brandSlug, productSlug, variantSlug } =
    useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const { selectedCity, findingCity, findCityError } = useSelector(
    (state) => state.city,
  );
  const cityId = selectedCity?.id;

  const {
    product,
    brand,
    category: categoryData,
    variations,
    loading,
    error,
    selectedVariant,
  } = useSelector((state) => state.product) || {
    product: null,
    brand: null,
    category: null,
    variations: [],
    loading: false,
    error: null,
    selectedVariant: null,
  };

  // ============= Handle City Selection =============
  useEffect(() => {
    if (citySlug) {
      const currentCitySlug = selectedCity?.slug;
      if (currentCitySlug !== citySlug) {
        dispatch(findCityBySlug(citySlug));
      }
    }
  }, [citySlug, selectedCity?.slug, dispatch]);

  // Fetch product data
  useEffect(() => {
    if (
      productSlug &&
      categorySlug &&
      brandSlug &&
      selectedCity?.slug === citySlug &&
      !findingCity &&
      !findCityError
    ) {
      dispatch(clearProductData());
      dispatch(
        fetchProductDetails({
          categorySlug,
          brandSlug,
          productSlug,
        }),
      );
    }

    return () => {
      dispatch(clearProductData());
    };
  }, [
    dispatch,
    productSlug,
    cityId,
    selectedCity?.slug,
    citySlug,
    findingCity,
    findCityError,
  ]);

  // Select variant based on URL slug
  useEffect(() => {
    if (variations.length > 0 && variantSlug) {
      const matchedVariant = variations.find((v) => v.slug === variantSlug);
      if (matchedVariant) {
        dispatch(setSelectedVariant(matchedVariant));
      }
    }
  }, [variations, variantSlug, dispatch]);

  const productPrice = useMemo(() => {
    if (selectedVariant?.price) return selectedVariant.price;
    if (product?.price) return product.price;
    if (variations?.length > 0) {
      const pricedVariant = variations.find((v) => v.price);
      return pricedVariant?.price || 0;
    }
    return 0;
  }, [selectedVariant, product, variations]);

  const productTitle = product?.title || productSlug?.replace(/-/g, " ") || "";
  const variationsCount = variations?.length || 0;
  // SEO
  const seoParams = useMemo(() => {
    if (loading || error || !product || !selectedCity) return null;

    let maxPrice = 0;
    if (variations?.length > 0) {
      const prices = variations.map((v) => v.price).filter((p) => p > 0);
      if (prices.length > 0) {
        maxPrice = Math.max(...prices);
      }
    }
    if (productPrice > maxPrice) maxPrice = productPrice;

    return {
      categorySlug: categorySlug,
      brandSlug: brandSlug,
      productSlug: productSlug,
      variantSlug: variantSlug,
      categoryName: categoryData?.name || "",
      brandName: brand?.name || "",
      productName: product?.title || "",
      variantName: selectedVariant?.name || "",
      variantPrice: selectedVariant?.price || 0,
      productDescription: product?.description || "",
      maxPrice: maxPrice,
      variantsCount: variations?.length || 0,
      cityName: selectedCity?.name || "",
      stateName: selectedCity?.state?.name || "",
      citySlug: selectedCity?.slug || "",
    };
  }, [
    loading,
    error,
    product,
    variations,
    productTitle,
    categoryData,
    brand,
    categorySlug,
    brandSlug,
    productSlug,
    variantSlug,
    variationsCount,
    productPrice,
    selectedVariant,
    selectedCity,
  ]);

  useSEO(
    selectedCity?.slug
      ? `/sell-used-online/in/${selectedCity.slug}/${categorySlug}/${brandSlug}/${productSlug}/${variantSlug}`
      : null,
    seoParams,
  );

  const handleVariantSelect = (variant) => {
    dispatch(setSelectedVariant(variant));
    // Navigate to the new city variant URL
    navigate(
      CITY_VARIANT_ROUTE(
        citySlug,
        categorySlug,
        brandSlug,
        productSlug,
        variant.slug,
      ),
      {
        replace: true,
      },
    );
  };

  const handleGetExactValue = () => {
    if (!selectedVariant) {
      toast.error("Please select a variant first");
      return;
    }

    if (!cityId) {
      toast.error("Please select your city first");
      return;
    }

    navigate(
      CALCULATOR_ROUTE(
        categorySlug,
        brandSlug,
        productSlug,
        selectedVariant.slug,
      ),
    );
  };

  // Show loading while city is being found or doesn't match
  if (
    loading ||
    findingCity ||
    !selectedCity ||
    selectedCity?.slug !== citySlug
  ) {
    return (
      <Layout>
        <div className="container px-4 py-6 mx-auto max-w-7xl">
          <ProductSkeleton isDarkMode={isDarkMode} />
        </div>
      </Layout>
    );
  }

  if (error || findCityError) {
    return (
      <Layout>
        <div className="container px-4 py-6 mx-auto max-w-7xl">
          <ErrorState
            error={error || findCityError}
            onRetry={() => {
              dispatch(
                fetchProductDetails({
                  categorySlug,
                  brandSlug,
                  productSlug,
                }),
              );
              if (citySlug && selectedCity?.slug !== citySlug) {
                dispatch(findCityBySlug(citySlug));
              }
            }}
            isDarkMode={isDarkMode}
          />
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="container px-4 py-6 mx-auto max-w-7xl">
          <EmptyState
            isDarkMode={isDarkMode}
            title="Product not found"
            message={`The product you're looking for doesn't exist or has been removed in ${selectedCity?.name}.`}
          />
        </div>
      </Layout>
    );
  }

  const mainImage = selectedVariant?.image || product.image;

  const renderContent = () => {
    return (
      <div
        className={`grid grid-cols-1 lg:grid-cols-2 gap-2 md:gap-6 p-4 lg:p-6 rounded-2xl ${
          isDarkMode ? "bg-gray-800" : "bg-white shadow-sm"
        }`}
      >
        <div className="grid grid-cols-3 gap-3 md:hidden">
          <ProductImage
            image={mainImage}
            title={selectedVariant?.name || product.title}
            isDarkMode={isDarkMode}
          />
          <div className="col-span-2">
            <ProductInfo
              title={product.title}
              subtitle={selectedVariant?.name}
              isDarkMode={isDarkMode}
              cityName={selectedCity?.name}
            />
          </div>
        </div>

        <div className="items-center justify-center hidden md:flex">
          <ProductImage
            image={mainImage}
            title={selectedVariant?.name || product.title}
            isDarkMode={isDarkMode}
          />
        </div>

        <div className="space-y-4">
          <div className="hidden md:block">
            <ProductInfo
              title={product.title}
              subtitle={selectedVariant?.name}
              isDarkMode={isDarkMode}
              cityName={selectedCity?.name}
            />
          </div>

          <ProductPriceCard
            productPrice={productPrice}
            isDarkMode={isDarkMode}
            selectedVariant={selectedVariant}
            cityName={selectedCity?.name}
          />

          {variations.length > 0 && (
            <ProductVariants
              variants={variations}
              selectedVariant={selectedVariant}
              onVariantSelect={handleVariantSelect}
              isDarkMode={isDarkMode}
            />
          )}

          <button
            onClick={handleGetExactValue}
            disabled={!selectedVariant}
            className={`w-full py-3 md:py-4 text-sm md:text-base font-medium rounded-xl flex items-center justify-center gap-2 transition-colors ${
              selectedVariant
                ? isDarkMode
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-emerald-500 hover:bg-emerald-600 text-white"
                : isDarkMode
                  ? "bg-gray-600 cursor-not-allowed text-gray-400"
                  : "bg-gray-300 cursor-not-allowed text-gray-500"
            }`}
          >
            <span>
              {selectedVariant ? "Get Exact Value" : "Select a Variant First"}
            </span>
            <FiArrowRight size={16} />
          </button>

          <ProductFeatures isDarkMode={isDarkMode} />
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="container px-4 py-6 mx-auto max-w-7xl">
        {renderContent()}
      </div>
    </Layout>
  );
};

export default CityVariant;
